'use client';

import { type ThreadMessage, useAuiState } from '@assistant-ui/react';

import { useHitl } from './hitl-provider';

interface HitlPartShape {
  type?: string;
  toolName?: string;
  toolCallId?: string;
  result?: { status?: string } | unknown;
  approval?: { approved?: boolean; resolution?: string };
}

export type HitlCallLane = 'human' | 'approval';

// Include the HITL identity and outcome in the activity trail's memoization key.
export function findHitlCallKeyInParts({
  parts,
  humanToolNames,
}: {
  parts: readonly unknown[];
  humanToolNames: ReadonlySet<string>;
}): string {
  for (const part of parts as ReadonlyArray<HitlPartShape>) {
    if (part.type !== 'tool-call' || !part.toolCallId) continue;
    if (part.approval) {
      const resolved = part.approval.approved !== undefined || !!part.approval.resolution;
      return `${part.toolCallId}|${resolved ? 'responded' : ''}|approval`;
    }
    if (part.toolName && humanToolNames.has(part.toolName)) {
      const status = (part.result as { status?: string } | undefined)?.status ?? '';
      return `${part.toolCallId}|${status}|human`;
    }
  }
  return '';
}

/**
 * `findHitlCallKeyInParts` for the current message. A joined string keeps `useAuiState`
 * referentially stable across re-renders.
 */
export function useHitlCallKey(): string {
  const { humanToolNames } = useHitl();
  return useAuiState(({ message }) =>
    findHitlCallKeyInParts({ parts: message?.parts ?? [], humanToolNames }),
  );
}

export function parseHitlCallKey(key: string): {
  toolCallId: string;
  resultStatus: string;
  lane: HitlCallLane | '';
} {
  const [toolCallId = '', resultStatus = '', lane = ''] = key.split('|');
  return { toolCallId, resultStatus, lane: lane as HitlCallLane | '' };
}

export function isAwaitingHitlAction(status: ThreadMessage['status']): boolean {
  return status?.type === 'requires-action' && status.reason !== 'interrupt';
}

// A parked run is waiting for the user, so its activity clock must stop.
export function useAwaitingHitlAction(): boolean {
  return useAuiState(({ message }) => isAwaitingHitlAction(message?.status));
}
